import {
  ClipboardEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import { LEAD_NAME_MAX_LENGTH } from '@constants/generic';

const getNameShortenedWarning = (maxLength: number): string =>
  `Name shortened to fit the ${maxLength} character limit.`;

interface UseNameLengthWarningOptions {
  maxLength?: number;
  hasError?: boolean;
}

// Hook to handle name length warning
const useNameLengthWarning = ({
  maxLength = LEAD_NAME_MAX_LENGTH,
  hasError = false
}: UseNameLengthWarningOptions = {}) => {
  const [showWarning, setShowWarning] = useState(false);
  const pasteJustTruncatedRef = useRef(false);

  useEffect(() => {
    if (hasError) {
      setShowWarning(false);
    }
  }, [hasError]);

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      const pastedText = event.clipboardData.getData('text');
      const input = event.currentTarget;
      const selectionLength =
        (input.selectionEnd ?? 0) - (input.selectionStart ?? 0);
      const resultingLength =
        input.value.length - selectionLength + pastedText.length;

      if (resultingLength > maxLength) {
        pasteJustTruncatedRef.current = true;
        setShowWarning(true);
      }
    },
    [maxLength]
  );

  // Any manual edit dismisses the warning.
  const handleChange = useCallback(() => {
    if (pasteJustTruncatedRef.current) {
      pasteJustTruncatedRef.current = false;
      return;
    }
    setShowWarning(false);
  }, []);

  // Dismiss when focus moves to another focusable control
  const handleBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
    if (event.relatedTarget) {
      setShowWarning(false);
    }
  }, []);

  const resetWarning = useCallback(() => {
    pasteJustTruncatedRef.current = false;
    setShowWarning(false);
  }, []);

  return {
    maxLength,
    showWarning: showWarning && !hasError,
    warningMessage: getNameShortenedWarning(maxLength),
    handlePaste,
    handleChange,
    handleBlur,
    resetWarning
  };
};

export default useNameLengthWarning;
